import { VariantProps } from 'class-variance-authority';
import { ComponentPropsWithoutRef, forwardRef } from 'react';
import { WithIcon } from '../../types/component.types';
import { button } from './base';

export type ButtonProps = VariantProps<typeof button> &
  Omit<ComponentPropsWithoutRef<'button'>, 'className'> &
  WithIcon;

/**
 * Basic buttons are default buttons with text labels. Use sentence case for all button labels. A basic button can have text label only, with a leading icon, or with a trailing icon.
 *
 * @example
 * ```tsx
 * <Button intent="secondary">Click me!</Button>
 * ```
 */
// eslint-disable-next-line react-x/no-forward-ref -- TODO: Wait! Don't change this file without seeing if you can remove this disable. It was added during a bulk version update, but fixing it would leave the park nicer than you found it.
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      intent,
      purpose,
      size,
      width,
      textWrap,
      type = 'button',
      leadingIcon,
      trailingIcon,
      children,
      disabled,
      onClick,
      ...buttonProps
    },
    ref,
  ) => {
    return (
      <button
        type={type}
        ref={ref}
        className={button({ intent, purpose, size, width, textWrap })}
        aria-disabled={disabled}
        data-intent={intent}
        onClick={disabled ? (e) => e.preventDefault() : onClick}
        {...buttonProps}
      >
        {leadingIcon && <div aria-hidden>{leadingIcon}</div>}
        {children}
        {trailingIcon && <div aria-hidden>{trailingIcon}</div>}
      </button>
    );
  },
);

Button.displayName = 'Button';
